/**
 * Locker select modal.
 * Pass CTA as children to this component and it will open locker selection modal.
 * Adding on click to children will trigger action after locker selection logic is completed.
 */

import { Alert, Autocomplete, Button, CircularProgress, Grid, TextField } from '@mui/material';
import classNames from 'classnames';
import { PhoneInput, PhoneInputData } from 'components/PhoneInput/PhoneInput';
import lodash from 'lodash';
import CheckLgIcon from 'public/icons/icn-check-lg.svg';
import InfoLgIcon from 'public/icons/icn-info-lg.svg';
import LockerSelectionEditIcon from 'public/icons/locker-selection-icons/edit.svg';
import LockerSelectionLocationIcon from 'public/icons/locker-selection-icons/location-outline.svg';
import LockerSelectionLockIcon from 'public/icons/locker-selection-icons/lock.svg';
import LockerSelectionOpenLockIcon from 'public/icons/locker-selection-icons/lock-open-outline.svg';
import LockerSelectionPackageIcon from 'public/icons/locker-selection-icons/package.svg';
import LockerSelectionTimeIcon from 'public/icons/locker-selection-icons/time-outline.svg';
import React, { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useGlobalContext from '../../hooks/useGlobalContext';
import DeliveryService from '../../services/delivery.service';
import OrderService from '../../services/order.service';
import { useAppSelector } from '../../store/hooks';
import { IDeliveryProvince, ITerminalLocation } from '../../types/delivery.types';
import { DeliveryMethodType } from '../../types/delivery-method.types';
import { OrderType } from '../../types/order.types';
import { bundleContext, getBundleItemsCount, hasBundleItemsCount } from '../../utils/bundle-items';
import { formatPudoLockerOpeningHours } from '../../utils/formatPudoLockerOpeningHours';
import { useNotification } from '../../utils/snackbar';
import validate from '../../utils/validate';
import Modal from '../Modal/Modal';

type LockerSelectModalProps = {
  children: ReactElement;
  order: OrderType;
  onDoorLockRequested: (updatedOrder: OrderType) => void;
};

const PudoLockerSelectModalSection = {
  INFO: 'INFO',
  SELECT: 'SELECT',
  CONFIRM: 'CONFIRM',
  DONE: 'DONE',
} as const;

type LockerSelectModalSections = (typeof PudoLockerSelectModalSection)[keyof typeof PudoLockerSelectModalSection];

const deliveryService = new DeliveryService();
const orderService = new OrderService();

export const PudoLockerSelectModal = (props: LockerSelectModalProps) => {
  const { children: selectLockerButton, order, onDoorLockRequested } = props;
  const { t } = useTranslation();
  const { get, set } = useGlobalContext();
  const { showErrorNotification } = useNotification();
  const user = lodash.cloneDeep(useAppSelector(({ userData }) => userData?.user));

  const [isModalOpen, setModalOpen] = useState(false);
  const [deliveryLocations, setDeliveryLocations] = [
    get('pudoDeliveryLocations') as IDeliveryProvince[],
    (pudoDeliveryLocations) => set({ pudoDeliveryLocations }),
  ];
  const [section, setSection] = useState<LockerSelectModalSections>(PudoLockerSelectModalSection.INFO);
  const [errors, setErrors] = useState<{ phone?: string; [key: string]: string | boolean }>({});
  const [updatedOrder, setUpdatedOrder] = useState<OrderType>(null);

  const [formData, setFormData] = useState<{
    phone: string;
    province: IDeliveryProvince;
    locker: ITerminalLocation;
  }>(getInitialFormData());
  const [isLoading, setLoading] = useState(false);

  const [doorCode, setDoorCode] = useState<string>(null);

  const icons = [
    <LockerSelectionLocationIcon key="location" />,
    <LockerSelectionLockIcon key="lock" />,
    <LockerSelectionEditIcon key="edit" />,
    <LockerSelectionPackageIcon key="package" />,
  ];

  // DEV-2050 - a live bundle swaps each string for its _bundle wording, which names the count.
  const bundleItemsCount = getBundleItemsCount(order);
  const bundleTOptions = { context: bundleContext(bundleItemsCount), count: bundleItemsCount ?? undefined };

  const content = {
    [PudoLockerSelectModalSection.INFO]: {
      title: t('components.order-block.locker-select-modal.info.title'),
      info: t('components.order-block.locker-select-modal.info.explanation.title'),
      bullets: (
        (t('components.order-block.locker-select-modal.info.explanation.bullets', {
          returnObjects: true,
          ...bundleTOptions,
        }) || []) as string[][]
      ).map(([title, text], index) => ({
        icon: icons[index],
        title,
        text,
      })),
      cta: t('components.order-block.locker-select-modal.info.ok'),
    },
    [PudoLockerSelectModalSection.SELECT]: {
      title: t('components.order-block.locker-select-modal.select.title'),
      info: t('components.order-block.locker-select-modal.select.info', bundleTOptions),
      cta: t('components.order-block.locker-select-modal.select.ok', bundleTOptions),
    },
    [PudoLockerSelectModalSection.CONFIRM]: {
      title: t('components.order-block.locker-select-modal.confirm.title'),
      note: t('components.order-block.locker-select-modal.confirm.note', bundleTOptions),
      noteBundleBox: hasBundleItemsCount(bundleItemsCount)
        ? t('components.order-block.locker-select-modal.confirm.note-bundle-box', { count: bundleItemsCount })
        : null,
      noteDescription: t('components.order-block.locker-select-modal.confirm.note-description', bundleTOptions),
      cta: t('components.order-block.locker-select-modal.confirm.ok'),
    },
    [PudoLockerSelectModalSection.DONE]: {
      title: t('components.order-block.locker-select-modal.done.title'),
      info: t('components.order-block.locker-select-modal.done.info', bundleTOptions),
      cta: t('components.order-block.locker-select-modal.done.ok'),
    },
  };

  const SelectLockerButton = React.cloneElement(selectLockerButton, {
    onClick: () => {
      setModalOpen(true);

      // Run initial onClick if button has it.
      selectLockerButton?.props?.onClick?.();
    },
  });

  function getInitialFormData(provinces = deliveryLocations) {
    const [prefillProvinceStr, prefillLockerStr] = (user.shippingPrefillInfo?.pudo_seller?.address || '')
      ?.split('|')
      ?.map((data) => data.trim());

    const prefillProvince = provinces?.find((province) => province.name?.toLowerCase() === prefillProvinceStr.toLowerCase());
    const prefillLocker = prefillProvince?.locations?.find((location) => location.name?.toLowerCase() === prefillLockerStr.toLowerCase());

    return {
      phone: user?.phone || '',
      province: prefillProvince || null,
      locker: prefillLocker || null,
    };
  }

  const isFormValid = (): boolean => {
    setErrors({});

    // Validate
    const validation = validate(formData, {
      phone: {
        presence: {
          allowEmpty: false,
          message: t('components.order-block.locker-select-modal.select.form.phone-invalid'),
        },
      },
      province: {
        presence: {
          allowEmpty: false,
          message: t('components.order-block.locker-select-modal.select.form.province-invalid'),
        },
      },
      locker: {
        presence: {
          allowEmpty: false,
          message: t('components.order-block.locker-select-modal.select.form.locker-invalid'),
        },
      },
    });

    // Catch errors
    if (validation.hasErrors) {
      setErrors(validation?.errors);
      showErrorNotification({
        message: t('common.mandatory-fields-error'),
        error: null,
      });
    }

    return !validation.hasErrors;
  };

  const onClickCta = async () => {
    if (section === PudoLockerSelectModalSection.INFO) {
      setSection(PudoLockerSelectModalSection.SELECT);
    } else if (section === PudoLockerSelectModalSection.SELECT) {
      if (!isFormValid()) {
        return;
      }
      setSection(PudoLockerSelectModalSection.CONFIRM);
    } else if (section === PudoLockerSelectModalSection.CONFIRM) {
      const submitted = await submit();

      // On successful submit.
      if (submitted) {
        setSection(PudoLockerSelectModalSection.DONE);

        return;
      } else {
        // Go to "select" view if failed.
        setSection(PudoLockerSelectModalSection.SELECT);
      }
    } else if (section === PudoLockerSelectModalSection.DONE) {
      onCloseModal();
    }
  };

  const getProvinces = async () => {
    let locations = lodash.cloneDeep(deliveryLocations || []);

    if (!deliveryLocations?.length) {
      locations = (await deliveryService.getDeliveryLocations(DeliveryMethodType.PUDO, user?.country)) as IDeliveryProvince[];

      setDeliveryLocations(locations || []);
    }

    return locations;
  };

  const handleProvinceChange = (_, option: { title: unknown; value: string | number }) => {
    const selectedProvince = (deliveryLocations as IDeliveryProvince[]).find(({ name }) => name === (option?.title as string));

    setErrors({
      ...errors,
      province: null,
    });
    setFormData({
      ...formData,
      province: selectedProvince,
      locker: formData.province?.name === selectedProvince?.name ? formData.locker : null,
    });
  };

  const handleLockerChange = (_, option: { title: string; value: string }) => {
    const selectedLocker = formData.province?.locations?.find(({ id }) => {
      return `${id}` === `${option?.value}`;
    });

    setErrors({
      ...errors,
      locker: null,
    });
    setFormData({
      ...formData,
      locker: selectedLocker,
    });
  };

  const handlePhoneChange = (phone: PhoneInputData) => {
    setErrors({
      ...errors,
      phone: phone.raw ? null : t('components.order-block.locker-select-modal.select.form.phone-invalid'),
    });
    setFormData({
      ...formData,
      phone: phone.raw,
    });
  };

  const submit = async (): Promise<boolean> => {
    setLoading(true);

    // Submit
    try {
      const newUpdatedOrder = await orderService.requestOrderDoorCode(
        order.id,
        {
          fromTerminalId: formData.locker.id,
          phone: formData.phone,
        },
        {
          silentFail: true,
        },
      );

      // Check if we got the door code.
      if (!newUpdatedOrder.shipping?.doorcode) {
        console.error('Failed to create doorcode', newUpdatedOrder);
        throw new Error(t('common.general-error'));
      }

      setDoorCode(newUpdatedOrder.shipping?.doorcode);
      setLoading(false);
      setUpdatedOrder(newUpdatedOrder);

      return true;
    } catch (error) {
      const phoneError = error.data?.data?.errors?.phone;
      const isKnownError = !!phoneError || ['NO_SPACE_AT_TERMINAL', 'TERMINAL_UNAVAILABLE'].includes(error.data?.data?.errorCode);

      setErrors({
        ...errors,
        noSpace: error.data?.data?.errorCode === 'NO_SPACE_AT_TERMINAL',
        terminalUnavailable: error.data?.data?.errorCode === 'TERMINAL_UNAVAILABLE',
        phone: phoneError?.message,
      });

      // For errors that we do not have a special handing applied, show general error toast.
      if (!isKnownError) {
        showErrorNotification({
          error,
        });
      }

      setLoading(false);

      return false;
    }
  };

  const onCloseModal = () => {
    onDoorLockRequested?.(updatedOrder);
    setModalOpen(false);
  };

  // On modal open, show always info section.
  useEffect(() => {
    if (isModalOpen) {
      (async () => {
        const locations = await getProvinces();

        setSection(PudoLockerSelectModalSection.INFO);
        setFormData({
          ...getInitialFormData(locations),
        });
        setErrors({});
      })();
    }
  }, [isModalOpen]);

  return (
    <>
      {SelectLockerButton}

      <Modal
        open={isModalOpen}
        title={content[section]?.title}
        onClose={onCloseModal}
        disableBackdropDismiss
        closeIconVisible
        maxWidth="xs"
        className="locker-select-modal"
        actions={[
          <Button
            variant="contained"
            key="locker-select-modal-action"
            color="primary"
            size="large"
            className={classNames('locker-modal-cta', { loading: isLoading })}
            onClick={onClickCta}
            fullWidth={true}
            disabled={isLoading}
            startIcon={isLoading && <CircularProgress color="inherit" size={16} />}
          >
            {content[section]?.cta}
          </Button>,
        ]}
      >
        {/* INFO. */}
        {section === PudoLockerSelectModalSection.INFO && (
          <Grid container>
            <div className="locker-select-modal-content">
              <div
                className="locker-select-modal-content-title"
                dangerouslySetInnerHTML={{ __html: content[PudoLockerSelectModalSection.INFO].info }}
              />

              <div className="locker-select-modal-content-bullets">
                {content[PudoLockerSelectModalSection.INFO].bullets.map((bullet) => (
                  <div className="bullet" key={bullet.title}>
                    <div className="bullet-icon">{bullet.icon}</div>
                    <div className="bullet-content">
                      <div className="bullet-title">{bullet.title}</div>
                      <div className="bullet-text">
                        <span dangerouslySetInnerHTML={{ __html: bullet.text }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Grid>
        )}

        {/* SELECTION. */}
        {section === PudoLockerSelectModalSection.SELECT && (
          <Grid container>
            <div className="locker-select-modal-content">
              <div
                className="locker-select-modal-content-title"
                dangerouslySetInnerHTML={{ __html: content[PudoLockerSelectModalSection.SELECT].info }}
              />

              <div className="locker-select-modal-content-body">
                {/* Province */}
                <Autocomplete
                  noOptionsText={t('components.not-found.target.options')}
                  selectOnFocus={true}
                  openOnFocus={true}
                  autoSelect={true}
                  value={{
                    title: formData.province?.name,
                    value: formData.province?.name,
                  }}
                  onChange={handleProvinceChange}
                  isOptionEqualToValue={({ title }) => title === formData.province?.name}
                  getOptionLabel={({ title }) => (title as string) || ''}
                  options={deliveryLocations.map((deliveryLocation) => ({
                    title: deliveryLocation?.name,
                    value: deliveryLocation?.name,
                  }))}
                  renderInput={(params) => (
                    <TextField
                      variant="standard"
                      {...params}
                      label={t('components.order-block.locker-select-modal.select.form.province') + ':*'}
                      placeholder={t('components.order-block.locker-select-modal.select.form.province-placeholder')}
                      value={formData.province?.name || ''}
                      error={!!errors?.province}
                      helperText={errors?.province}
                      InputLabelProps={{
                        style: { pointerEvents: 'auto' },
                        shrink: true,
                      }}
                    />
                  )}
                />

                {/* Locker. */}
                <Autocomplete
                  noOptionsText={t('components.not-found.target.options')}
                  selectOnFocus={true}
                  openOnFocus={true}
                  className="mt-2-05-i"
                  autoSelect={true}
                  value={{
                    title: formData.locker?.name,
                    value: formData.locker?.id,
                  }}
                  onChange={handleLockerChange}
                  isOptionEqualToValue={({ value }) => value === formData.locker?.id}
                  getOptionLabel={({ title }) => (title as string) || ''}
                  options={(formData?.province?.locations || []).map((location) => ({
                    title: location?.name,
                    value: location?.id,
                  }))}
                  disabled={!formData.province}
                  renderInput={(params) => {
                    return (
                      <TextField
                        variant="standard"
                        {...params}
                        label={t('components.order-block.locker-select-modal.select.form.locker') + ':*'}
                        placeholder={t('components.order-block.locker-select-modal.select.form.locker-placeholder')}
                        value={formData.locker?.name || ''}
                        disabled={!formData.province}
                        error={!!errors?.locker}
                        helperText={errors?.locker}
                        InputLabelProps={{
                          style: { pointerEvents: 'auto' },
                          shrink: true,
                        }}
                      />
                    );
                  }}
                />

                {/* Locker info. */}
                {formData.locker?.id && (
                  <div className="locker-select-modal-content-bullets mt-2-05">
                    <div className="bullet">
                      <div className="bullet-icon">
                        <LockerSelectionLocationIcon key="location" />
                      </div>
                      <div className="bullet-content">
                        <div className="bullet-title text-normal padding-top-2px">{formData.locker?.address}</div>
                      </div>
                    </div>

                    {formData.locker?.openinghours && (
                      <div className="bullet">
                        <div className="bullet-icon">
                          <LockerSelectionTimeIcon key="time" />
                        </div>
                        <div className="bullet-content">
                          <div className="bullet-title text-normal pre-line padding-top-2px">
                            {t('components.order-block.locker-select-modal.select.form.locker-hours', {
                              hours: formatPudoLockerOpeningHours(formData.locker?.openinghours),
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* No space error. */}
                {errors.noSpace && (
                  <Alert severity="error" variant="standard" className="mt-2-05 color-danger">
                    {t('components.order-block.locker-select-modal.select.form.no-space')}
                  </Alert>
                )}

                {/* Terminal unavailable error. */}
                {errors.terminalUnavailable && (
                  <Alert severity="error" variant="standard" className="mt-2-05 color-danger">
                    {t('components.order-block.locker-select-modal.select.form.terminal-unavailable')}
                  </Alert>
                )}

                {/* Phone number. */}
                <div className="mt-2-05-i">
                  <PhoneInput
                    value={formData?.phone || ''}
                    onChange={handlePhoneChange}
                    error={!!errors?.phone}
                    helperText={errors?.phone}
                  />
                </div>
              </div>
            </div>
          </Grid>
        )}

        {/* CONFIRM. */}
        {section === PudoLockerSelectModalSection.CONFIRM && (
          <Grid container>
            <div className="locker-select-modal-content">
              <div className="locker-select-modal-content-icon">
                <InfoLgIcon />
              </div>

              <div
                className="locker-select-modal-content-note mt-3"
                dangerouslySetInnerHTML={{ __html: content[PudoLockerSelectModalSection.CONFIRM].note }}
              />

              {content[PudoLockerSelectModalSection.CONFIRM].noteBundleBox && (
                <div className="locker-select-modal-content-note-description mt-3">
                  {content[PudoLockerSelectModalSection.CONFIRM].noteBundleBox}
                </div>
              )}

              <div
                className="locker-select-modal-content-note-description mt-3"
                dangerouslySetInnerHTML={{ __html: content[PudoLockerSelectModalSection.CONFIRM].noteDescription }}
              />
            </div>
          </Grid>
        )}

        {/* DONE. */}
        {section === PudoLockerSelectModalSection.DONE && (
          <Grid container>
            <div className="locker-select-modal-content">
              <div className="locker-select-modal-content-icon">
                <CheckLgIcon />
              </div>

              <div
                className="locker-select-modal-content-title mt-3"
                dangerouslySetInnerHTML={{ __html: content[PudoLockerSelectModalSection.DONE].info }}
              />

              <div className="locker-select-modal-content-bullets mt-2-05">
                <div className="bullet">
                  <div className="bullet-icon">
                    <LockerSelectionOpenLockIcon key="lock" />
                  </div>
                  <div className="bullet-content">
                    <div className="bullet-title text-normal pre-line padding-top-2px">
                      <b>{t('components.order-block.locker-select-modal.done.form.locker-code')}:</b> {doorCode}
                    </div>
                  </div>
                </div>

                <div className="bullet">
                  <div className="bullet-icon">
                    <LockerSelectionLocationIcon key="location" />
                  </div>
                  <div className="bullet-content">
                    <div className="bullet-title">{t('components.order-block.locker-select-modal.done.form.locker')}:</div>
                    <div className="bullet-text">{formData.locker?.address}</div>
                  </div>
                </div>

                {formData.locker?.openinghours && (
                  <div className="bullet">
                    <div className="bullet-icon">
                      <LockerSelectionTimeIcon key="time" />
                    </div>
                    <div className="bullet-content">
                      <div
                        className="bullet-title text-normal pre-line padding-top-2px"
                        dangerouslySetInnerHTML={{
                          __html: t('components.order-block.locker-select-modal.done.form.locker-hours', {
                            hours: formatPudoLockerOpeningHours(formData.locker?.openinghours),
                          }),
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Grid>
        )}
      </Modal>
    </>
  );
};
